/**
 * navigation.ts — the one list of places in the CMS. The sidebar shows NAV_GROUPS and nothing else: every item is its own
 * route, there is no second row of tabs under a page and no hub page that lists sub-pages.
 * `managerOnly` is the fixed gate for the most sensitive screens; `capability` is the manager-editable gate.
 */
import type { LucideIcon } from "lucide-react";
import {
  BarChart3, BookOpenCheck, CalendarDays, CircleDollarSign, Database, FileDown, FileText, GitBranch, History, Inbox, KeyRound, Layers, Lightbulb, MessageSquareText,
  Megaphone, PlugZap, ScrollText, Search, Settings2, Share2, Sparkles, Tags, Users, Wand2, Waypoints, Send, Target,
} from "lucide-react";
import type { Capability } from "@demandhub-cms/shared";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  managerOnly?: boolean;
  capability?: Capability;
  /** Other paths that count as being "inside" this item. */
  alsoMatches?: readonly string[];
  /** Shows the number of things waiting (ads + posts) on the Review item. */
  badge?: "review";
}

export interface NavGroup {
  title: string | null;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    title: "Content",
    items: [
      { to: "/content", label: "Articles", icon: FileText, alsoMatches: [] },
      { to: "/ai/drafts", label: "AI drafts", icon: Wand2, capability: "ai.use" },
      { to: "/ai/ideas", label: "Topic ideas", icon: Lightbulb, capability: "ai.use" },
      { to: "/calendar", label: "Calendar", icon: CalendarDays },
      { to: "/content/taxonomy", label: "Taxonomy", icon: Tags, capability: "taxonomy.manage", alsoMatches: ["/content/categories", "/content/authors"] },
      { to: "/git", label: "Site & Git", icon: GitBranch, managerOnly: true, alsoMatches: ["/settings/variables", "/settings/sites"] },
      { to: "/ai/memory", label: "Brand memory", icon: BookOpenCheck, capability: "ai.use" },
      { to: "/ai/evidence", label: "Evidence", icon: Database },
    ],
  },
  { title: null, items: [{ to: "/review", label: "Review", icon: Inbox, capability: "content.publish", badge: "review" }] },
  {
    title: "Ads",
    items: [
      { to: "/ads/this-week", label: "This week", icon: Waypoints, capability: "ai.use" },
      { to: "/ads/concepts", label: "Concepts", icon: Lightbulb, capability: "ai.use" },
      { to: "/ads/ads", label: "Ads", icon: Megaphone, capability: "ai.use" },
      { to: "/ads/research", label: "Research", icon: Search, capability: "ai.use" },
      { to: "/ads/google", label: "Google", icon: Target, capability: "ai.use" },
      { to: "/ads/results", label: "Results", icon: BarChart3, capability: "ai.use" },
      { to: "/ads/context", label: "Context", icon: Layers, capability: "ai.use" },
    ],
  },
  {
    title: "Social",
    items: [
      { to: "/social/this-week", label: "This week", icon: Waypoints },
      { to: "/social/ideas", label: "Ideas", icon: Sparkles },
      { to: "/social/posts", label: "Posts", icon: Send },
      { to: "/social/channels", label: "Channels", icon: Share2 },
      { to: "/social/results", label: "Results", icon: BarChart3 },
      { to: "/social/context", label: "Context", icon: Layers },
    ],
  },
  {
    title: "Operations",
    items: [
      { to: "/ops/runs", label: "Runs", icon: History, capability: "ai.use" },
      { to: "/ops/costs", label: "Costs", icon: CircleDollarSign, capability: "ai.use", alsoMatches: ["/ai/usage"] },
      { to: "/ops/imports", label: "Imports", icon: FileDown, capability: "ai.use" },
      { to: "/ops/integrations", label: "Integrations", icon: PlugZap, managerOnly: true },
      { to: "/audit", label: "Audit", icon: ScrollText, managerOnly: true },
    ],
  },
  {
    title: "Settings",
    items: [
      { to: "/settings/products", label: "Products", icon: Settings2, managerOnly: true, alsoMatches: ["/ai/pictures"] },
      { to: "/users", label: "People & roles", icon: Users, managerOnly: true, alsoMatches: ["/settings/permissions"] },
      { to: "/settings/api-keys", label: "API keys", icon: KeyRound, managerOnly: true },
      { to: "/settings/prompts", label: "Prompts", icon: MessageSquareText, managerOnly: true },
    ],
  },
];

export const ALL_NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/** The item whose route the current path belongs to; the longest matching route wins. */
export function currentItem(items: readonly NavItem[], pathname: string): NavItem | undefined {
  let best: NavItem | undefined;
  let bestLen = -1;
  for (const item of items) {
    for (const p of [item.to, ...(item.alsoMatches ?? [])]) {
      if ((pathname === p || pathname.startsWith(`${p}/`)) && p.length > bestLen) {
        best = item;
        bestLen = p.length;
      }
    }
  }
  return best;
}
